import { createContext, useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { z } from 'zod';
import { FieldValueSchema } from '@almadar/core';
import { jsx } from 'react/jsx-runtime';

// src/react/index.tsx
var AuthContext = createContext(void 0);
var NOT_CONFIGURED = "Sign-in is not configured for this app";
function AuthProvider({ connect, children }) {
  const [auth, setAuth] = useState(null);
  const [user, setUser] = useState(null);
  const [authority, setAuthority] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let unsubscribe;
    let cancelled = false;
    connect().then((connected) => {
      if (cancelled) return;
      setAuth(connected);
      if (connected === null) {
        setAuthority("unavailable");
        setLoading(false);
        return;
      }
      unsubscribe = connected.onUserChanged(
        (next) => {
          setUser(next);
          setAuthority(next === null ? "anonymous" : "authenticated");
          setLoading(false);
        },
        (err) => {
          setUser(null);
          setAuthority("failed");
          setError(err.message);
          setLoading(false);
        }
      );
    }).catch((err) => {
      if (cancelled) return;
      setAuthority("failed");
      setError(err.message);
      setLoading(false);
    });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [connect]);
  const run = useCallback(async (action) => {
    if (auth === null) {
      setError(NOT_CONFIGURED);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await action(auth);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [auth]);
  const value = useMemo(() => ({
    auth,
    user,
    authority,
    loading,
    error,
    clearError: () => setError(null),
    signInWithGoogle: () => run((a) => a.signInWithGoogle()),
    signInWithEmail: (email, password) => run((a) => a.signInWithEmail(email, password)),
    signUpWithEmail: (email, password, displayName) => run((a) => a.signUpWithEmail(email, password, displayName)),
    sendSignInLinkToEmail: (email, returnUrl) => run((a) => a.sendSignInLinkToEmail(email, returnUrl)),
    isSignInWithEmailLink: (link) => auth?.isSignInWithEmailLink(link) ?? false,
    signInWithEmailLink: (email, link) => run((a) => a.signInWithEmailLink(email, link)),
    signInWithCustomToken: (customToken) => run((a) => a.signInWithCustomToken(customToken)),
    signOut: () => run((a) => a.signOut())
  }), [auth, user, authority, loading, error, run]);
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value, children });
}
function useOptionalAuth() {
  return useContext(AuthContext);
}
function useAuth() {
  const value = useContext(AuthContext);
  if (value === void 0) throw new Error("useAuth must be used within an AuthProvider");
  return value;
}
var PersonasSchema = z.object({
  success: z.literal(true),
  personas: z.array(z.object({ id: z.string() }).catchall(FieldValueSchema.optional()))
});
var SignInSchema = z.object({ success: z.literal(true), customToken: z.string() });
function useDevPersonas(apiBase) {
  const { signInWithCustomToken } = useAuth();
  const [personas, setPersonas] = useState([]);
  useEffect(() => {
    let cancelled = false;
    void fetch(`${apiBase}/api/personas`).then(async (res) => res.ok ? PersonasSchema.parse(await res.json()).personas : []).then((list) => {
      if (!cancelled) setPersonas(list);
    });
    return () => {
      cancelled = true;
    };
  }, [apiBase]);
  const signInAs = useCallback(async (id) => {
    const res = await fetch(`${apiBase}/api/personas/sign-in`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id })
    });
    await signInWithCustomToken(SignInSchema.parse(await res.json()).customToken);
  }, [apiBase, signInWithCustomToken]);
  return { personas, signInAs };
}

export { AuthProvider, useAuth, useDevPersonas, useOptionalAuth };
//# sourceMappingURL=react.js.map
//# sourceMappingURL=react.js.map